import React, { useState, useEffect } from 'react'
import { noop } from 'lodash'
import _cx from 'classnames/bind'
import PropTypes from 'prop-types'
import GoogleMap from 'google-map-react'
import { fitBounds } from 'google-map-react/utils'
import config from '@adc/configment'
import { Loader } from '@adc/ui-adc'
import { helpers } from '@adc/core-utils'
import pinSelected from '../../images/map_pin_hover.svg'
import pinUnselected from '../../images/map_pin.svg'
import cls from './index.scss'
const cx = _cx.bind(cls)

const GOOGLE_MAPS_MIN_WIDTH = 350
const GOOGLE_MAPS_MIN_HEIGHT = 265

// We need this as it's own component in order to suppress warnings
// https://github.com/google-map-react/google-map-react/issues/213
const Pin = ({
  src,
  active,
  lat,
  lng,
  select = noop,
  deSelect = noop,
  ...props
}) => (
  <img
    src={src}
    className={cx('pin', {
      active,
    })}
    lat={lat} // eslint-disable-line react/no-unknown-property
    lng={lng} // eslint-disable-line react/no-unknown-property
    onClick={select}
    onMouseEnter={select}
    onMouseLeave={deSelect}
    {...props}
  />
)

Pin.propTypes = {
  src: PropTypes.string.isRequired,
  active: PropTypes.bool,
  lat: PropTypes.number.isRequired,
  lng: PropTypes.number.isRequired,
  select: PropTypes.func,
  deSelect: PropTypes.func,
}

const getBounds = coords => {
  if (!window.google) {
    return {}
  }

  const bounds = new window.google.maps.LatLngBounds()

  coords.forEach(pin => {
    bounds.extend(new window.google.maps.LatLng(pin.lat, pin.lng))
  })

  const newBounds = {
    ne: {
      lat: bounds.getNorthEast().lat(),
      lng: bounds.getNorthEast().lng(),
    },
    sw: {
      lat: bounds.getSouthWest().lat(),
      lng: bounds.getSouthWest().lng(),
    },
  }

  return fitBounds(newBounds, {
    width: GOOGLE_MAPS_MIN_WIDTH,
    height: GOOGLE_MAPS_MIN_HEIGHT,
  })
}

const ComparableMap = ({
  coords,
  properties,
  selectPin,
  deSelectPin,
  selectedProperty,
  ...props
}) => {
  const [isGmapsLoaded, setIsGmapsLoaded] = useState(false)

  useEffect(() => {
    // WHY: google-map-react library loads the gmaps scripts when the component
    // is first rendered so if they are not in memory (window.google is not set)
    // before component finishes rendering map will not display until there
    // had been a state update that forces a refresh
    // (this is due to the way getBounds is written)
    const checkGmaps = setInterval(() => {
      if (helpers.environments.canUseDom() && window?.google) {
        setIsGmapsLoaded(true)
        clearInterval(checkGmaps)
      }
    }, 100)

    return () => clearInterval(checkGmaps)
  }, [])

  const pins = properties
    .map((property, i) => ({
      lat: property.info.geocode.latitude,
      lng: property.info.geocode.longitude,
      id: i,
    }))
    .sort((a, b) => a.lng - b.lng) // We sort the pins so that they overlap correctly on on the map, so pins on the bottom are always in front of pins above them

  // Sometimes we don't have coordinates for our own properties...
  const validCoords = Boolean(coords.lat) && Boolean(coords.lng)

  // Gets the center and zoom level that fits all pins in the screen
  const { center, zoom } = getBounds(validCoords ? pins.concat(coords) : pins)

  return (
    <div
      style={{ height: '300px', width: '100%', position: 'relative' }}
      className={cx('u-mt-1')}
    >
      {!isGmapsLoaded && (
        <Loader active inverted inline dimmer={false} elmId='gmaps_loading'>
          Loading
        </Loader>
      )}
      <GoogleMap
        center={center}
        zoom={zoom}
        bootstrapURLKeys={{
          client: config.get('GOOGLE_MAP_CLIENT_ID'),
          channel: config.get('GOOGLE_MAP_CHANNEL'),
          v: 3,
        }}
        options={{
          streetViewControl: true,
          zoomControl: true,
          scrollwheel: false,
          gestureHandling: 'greedy',
          fullscreenControl: false,
        }}
        {...props}
      >
        {validCoords && (
          <Pin src={pinSelected} {...coords} style={{ zIndex: -1 }} />
        )}
        {isGmapsLoaded &&
          pins.map(pin => (
            <Pin
              key={pin.id}
              src={pinUnselected}
              active={selectedProperty === pin.id}
              lat={pin.lat}
              lng={pin.lng}
              select={selectPin(pin.id)}
              deSelect={deSelectPin()}
            />
          ))}
      </GoogleMap>
    </div>
  )
}

ComparableMap.propTypes = {
  coords: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }).isRequired,
  properties: PropTypes.object.isRequired,
  selectPin: PropTypes.func.isRequired,
  deSelectPin: PropTypes.func.isRequired,
  selectedProperty: PropTypes.number.isRequired,
}

export default React.memo(ComparableMap)
